import { useEffect, useState, type CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import { siX, siWhatsapp, siReddit, siTelegram, siFacebook } from 'simple-icons';
import { useI18n } from '../i18n';

export default function ShareModal({
  title,
  text,
  url,
  embed,
  makeImage,
  onClose,
}: {
  title?: string;
  text: string;
  url: string;
  embed?: string;
  makeImage: () => Promise<string>;
  onClose: () => void;
}) {
  const { t } = useI18n();
  const [img, setImg] = useState('');
  const [msg, setMsg] = useState('');
  const full = `${text}\n${url}`;

  useEffect(() => {
    let on = true;
    makeImage().then((d) => on && setImg(d));
    return () => {
      on = false;
    };
    // 仅在打开时生成一次
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  function flash(m: string) {
    setMsg(m);
    setTimeout(() => setMsg(''), 1600);
  }
  async function copy(value: string) {
    try {
      await navigator.clipboard.writeText(value);
      flash(t.copied);
    } catch {
      /* ignore */
    }
  }
  function saveImage() {
    if (!img) return;
    const a = document.createElement('a');
    a.href = img;
    a.download = 'geodle.png';
    a.click();
  }
  async function nativeShare() {
    try {
      await navigator.share?.({ text: full });
    } catch {
      /* cancelled */
    }
  }

  const T = encodeURIComponent(full);
  const U = encodeURIComponent(url);
  const socials = [
    { name: 'X', icon: siX, href: `https://twitter.com/intent/tweet?text=${T}` },
    { name: 'WhatsApp', icon: siWhatsapp, href: `https://wa.me/?text=${T}` },
    { name: 'Reddit', icon: siReddit, href: `https://www.reddit.com/submit?title=GeoDle&text=${T}` },
    { name: 'Telegram', icon: siTelegram, href: `https://t.me/share/url?url=${U}&text=${encodeURIComponent(text)}` },
    { name: 'Facebook', icon: siFacebook, href: `https://www.facebook.com/sharer/sharer.php?u=${U}` },
  ];

  return createPortal(
    <div className="modal-scrim" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <button className="modal-x" onClick={onClose} aria-label="Close">
          ×
        </button>
        <h3>{title ?? t.share}</h3>

        {img && <img className="share-img" src={img} alt="" />}

        <pre className="share-text">{text}</pre>

        <div className="modal-actions">
          {typeof navigator !== 'undefined' && 'share' in navigator && (
            <button className="primary" onClick={nativeShare}>
              {t.share}
            </button>
          )}
          <button onClick={saveImage}>{t.saveImage}</button>
          <button onClick={() => copy(full)}>{t.copyResult}</button>
          <button onClick={() => copy(url)}>{t.copyLink}</button>
          {embed && <button onClick={() => copy(embed)}>{t.copyEmbed}</button>}
        </div>

        <div className="social-row">
          {socials.map((s) => (
            <a
              key={s.name}
              href={s.href}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={s.name}
              className="social"
              style={{ '--brand': `#${s.icon.hex}` } as CSSProperties}
            >
              <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
                <path d={s.icon.path} fill="currentColor" />
              </svg>
            </a>
          ))}
        </div>

        {msg && <div className="modal-msg">{msg}</div>}
      </div>
    </div>,
    document.body,
  );
}
